import React, { useState, useEffect, memo } from 'react';
import axios from 'axios';
import AdminSidebarRaw from '../Components/AdminSidebar';
import { API_BASE_URL } from '../config';
import '../Styles/AdminOrders.css';

const AdminSidebar = memo(AdminSidebarRaw);

const AdminOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [pendingStatusUpdate, setPendingStatusUpdate] = useState(null); // { orderId, newStatus }
    const [statusError, setStatusError] = useState(null);
    const [editingDeliveryFee, setEditingDeliveryFee] = useState(false);
    const [newDeliveryFee, setNewDeliveryFee] = useState('');
    const [savingDeliveryFee, setSavingDeliveryFee] = useState(false);

    const handleSendWhatsapp = (order) => {
        if (!order || !order.customer_phone) return;

        const isCancelled = order.status?.toLowerCase() === 'cancelled';
        const isExchange = order.order_number?.startsWith('EXC-');

        const itemsList = (order.items || []).map(item => {
            const variantParts = [item.variation_color, item.variation_size].filter(Boolean);
            if (item.product_type) variantParts.push(`Fabric: ${item.product_type}`);
            const variant = variantParts.length > 0 ? `(${variantParts.join(' / ')})` : '';

            if (item.offer_qty) {
                const offQty = Number(item.offer_qty) || 0;
                const offPrice = parseFloat(item.offer_price) || 0;
                const lineTotal = parseFloat(item.total).toFixed(2);
                return `${item.product_name} ${variant} x${item.quantity} (Offer Applied: ${offQty} for $${offPrice.toFixed(2)}) - $${lineTotal}`;
            }

            const itemPriceValue = parseFloat(item.price || 0);
            const itemPrice = itemPriceValue.toFixed(2);
            const itemDiscountValue = parseFloat(item.discount_value || 0);
            const itemDiscountType = item.discount_type;
            const discountedPriceValue = parseFloat(item.total || item.price || 0);
            const discountedPrice = discountedPriceValue.toFixed(2);
            const itemDiscountAmount = parseFloat(item.discount || 0);

            let discountInfo;
            if (itemDiscountValue > 0) {
                if (itemDiscountAmount > 0 && discountedPriceValue !== itemPriceValue) {
                    if (itemDiscountType === 'percentage') {
                        discountInfo = ` ($${itemPrice} → $${discountedPrice} (-${itemDiscountValue}% | -$${itemDiscountAmount.toFixed(2)}))`;
                    } else {
                        discountInfo = ` ($${itemPrice} → $${discountedPrice} (-$${itemDiscountAmount.toFixed(2)}))`;
                    }
                } else {
                    const discountLabel = itemDiscountType === 'percentage'
                        ? `-${itemDiscountValue}%`
                        : `-$${itemDiscountAmount > 0 ? itemDiscountAmount.toFixed(2) : itemDiscountValue.toFixed(2)}`;
                    discountInfo = ` ($${itemPrice} ${discountLabel})`;
                }
            } else {
                discountInfo = ` ($${itemPrice})`;
            }

            return `${item.product_name} ${variant} x${item.quantity}${discountInfo}`;
        }).join('\n') || '';

        const subtotal = parseFloat(order.total_amount || 0);
        const deliveryFee = parseFloat(order.delivery_fee || 0);
        const discountType = order.discount_type;
        const discountValue = parseFloat(order.discount_value || 0);
        const discountAmount = parseFloat(order.discount_amount || 0);

        const orderDiscountLabel = discountAmount > 0
            ? discountType === 'percentage'
                ? `Order Discount (-${discountValue}%): -$${discountAmount.toFixed(2)}`
                : `Order Discount: -$${discountAmount.toFixed(2)}`
            : '';

        const couponDiscountLabel = order.coupon_discount_value > 0
            ? order.coupon_discount_type === 'percentage'
                ? `Coupon Discount (${order.coupon_code} -${order.coupon_discount_value}%): -$${(subtotal * order.coupon_discount_value / 100).toFixed(2)}`
                : `Coupon Discount (${order.coupon_code}): -$${parseFloat(order.coupon_discount_value).toFixed(2)}`
            : '';

        const address = [
            order.customer_address1,
            order.customer_address2,
            order.customer_street,
            order.customer_building ? `Bldg: ${order.customer_building}` : null,
            order.customer_floor ? `Floor: ${order.customer_floor}` : null,
            order.customer_city,
            order.customer_country
        ].filter(Boolean).join(', ');

        const statusMsg = isCancelled ? 'CANCELLED' : 'confirmed';

        let messageHeader = `Hello ${order.customer_name || 'Customer'},\n`;
        if (isExchange) {
            messageHeader += `Your exchange request #${order.order_number} has been ${statusMsg}. Phone: ${order.customer_phone}\n`;
            if (order.original_order_number) {
                messageHeader += `Original Order: #${order.original_order_number}\n`;
            }
            messageHeader += `\n`;
        } else {
            messageHeader += `Your order #${order.order_number || order.id || 'N/A'} has been ${statusMsg}. Phone: ${order.customer_phone}\n\n`;
        }

        const message = messageHeader +
            `Shipping Address: ${address || 'N/A'}\n\n` +
            `${isExchange ? 'Exchanged Items' : 'Items'}:\n\n` +
            `${itemsList}\n` +
            `Subtotal: $${subtotal.toFixed(2)}\n` +
            `${deliveryFee > 0 ? `${isExchange ? 'Exchange Delivery Charge' : 'Delivery Fee'}: $${deliveryFee.toFixed(2)}\n` : ''}` +
            `${orderDiscountLabel ? `${orderDiscountLabel}\n` : ''}` +
            `${couponDiscountLabel ? `${couponDiscountLabel}\n` : ''}` +
            `Total Amount: $${parseFloat(order.final_amount || 0).toFixed(2)}\n\n` +
            (isCancelled
                ? `If you have any questions, please contact our support team. We apologize for any inconvenience.`
                : `Thank you for shopping with us!`);

        const encodedMsg = encodeURIComponent(message);
        const phone = order.customer_phone.replace(/\D/g, '');
        const whatsappUrl = `https://wa.me/${phone}?text=${encodedMsg}`;

        window.open(whatsappUrl, '_blank');
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/orders`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setOrders(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching orders:', err);
            setError('Failed to fetch orders');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = (orderId, newStatus) => {
        const order = orders.find(o => o.id === orderId);
        if (!order) return;

        // Validation: Cannot move back to pending if already confirmed or cancelled
        if (newStatus?.toLowerCase() === 'pending' &&
            (order.status?.toLowerCase() === 'confirmed' || order.status?.toLowerCase() === 'cancelled')) {
            setStatusError(`Order already ${order.status} and cannot be moved back to pending.`);
            return;
        }

        setPendingStatusUpdate({ orderId, newStatus });
    };

    const executeStatusUpdate = async () => {
        if (!pendingStatusUpdate) return;
        const { orderId, newStatus } = pendingStatusUpdate;
        const adminToken = localStorage.getItem('adminToken');

        try {
            setUpdatingStatus(true);
            await axios.put(`${API_BASE_URL}/orders/${orderId}`, { status: newStatus }, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });

            // Update local state
            setOrders(orders.map(o => o.id === orderId ? { ...o, status: newStatus } : o));

            const updatedOrder = orders.find(o => o.id === orderId);
            if (updatedOrder && (newStatus?.toLowerCase() === 'confirmed' || newStatus?.toLowerCase() === 'cancelled')) {
                handleSendWhatsapp({ ...updatedOrder, status: newStatus });
            }

            if (selectedOrder && selectedOrder.id === orderId) {
                setSelectedOrder({ ...selectedOrder, status: newStatus });
            }
            setPendingStatusUpdate(null);
        } catch (err) {
            console.error('Error updating status:', err);
            const errorMsg = err.response?.data?.message || 'Failed to update status';
            alert(errorMsg);
        } finally {
            setUpdatingStatus(false);
        }
    };

    const handleEditDeliveryFee = () => {
        setNewDeliveryFee(selectedOrder.delivery_fee || 0);
        setEditingDeliveryFee(true);
    };

    const handleSaveDeliveryFee = async () => {
        if (!selectedOrder) return;
        const adminToken = localStorage.getItem('adminToken');

        try {
            setSavingDeliveryFee(true);
            const updatedFee = parseFloat(newDeliveryFee) || 0;

            // Re-calculate final amount
            const oldDeliveryFee = parseFloat(selectedOrder.delivery_fee) || 0;
            const diff = updatedFee - oldDeliveryFee;
            const newFinalAmount = parseFloat(selectedOrder.final_amount || 0) + diff;

            await axios.put(`${API_BASE_URL}/orders/${selectedOrder.id}`, {
                delivery_fee: updatedFee,
                final_amount: newFinalAmount
            }, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });

            // Update local state
            const updatedOrder = { ...selectedOrder, delivery_fee: updatedFee, final_amount: newFinalAmount };
            setSelectedOrder(updatedOrder);
            setOrders(orders.map(o => o.id === selectedOrder.id ? updatedOrder : o));
            setEditingDeliveryFee(false);
        } catch (err) {
            console.error('Error updating delivery fee:', err);
            alert('Failed to update delivery fee');
        } finally {
            setSavingDeliveryFee(false);
        }
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const getStatusClass = (status) => {
        const s = status?.toLowerCase();
        if (s === 'confirmed') return 'admin-status-confirmed';
        return `admin-status-${s || 'pending'}`;
    };

    const filteredOrders = orders.filter(order => {
        const matchesStatus = !statusFilter || order.status?.toLowerCase() === statusFilter.toLowerCase();

        const matchesSearch = !searchTerm ||
            order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());

        const orderDate = new Date(order.created_at);
        const matchesFromDate = !fromDate || orderDate >= new Date(fromDate);
        // Set toDate to end of day to include all orders on that day
        const matchesToDate = !toDate || orderDate <= new Date(toDate + 'T23:59:59');

        return matchesStatus && matchesSearch && matchesFromDate && matchesToDate;
    });

    const IconClose = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
    );

    return (
        <div className="admin-orders-container">
            <AdminSidebar />

            <main className="admin-orders-main">

                <div className="admin-filters-bar">
                    <div className="admin-filter-group">
                        <label>Search</label>
                        <input
                            type="text"
                            placeholder="Order # or Customer"
                            className="admin-filter-input"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="admin-filter-group">
                        <label>Status</label>
                        <select
                            className="admin-filter-input"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>
                    <div className="admin-filters-row">
                        <div className="admin-filter-group">
                            <label>From Date</label>
                            <input
                                type="date"
                                className="admin-filter-input"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </div>
                        <div className="admin-filter-group">
                            <label>To Date</label>
                            <input
                                type="date"
                                className="admin-filter-input"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </div>
                    </div>
                    {(searchTerm || statusFilter || fromDate || toDate) && (
                        <div className="admin-filter-group">
                            <label>&nbsp;</label>
                            <button
                                className="admin-action-btn clear"
                                onClick={() => {
                                    setSearchTerm('');
                                    setStatusFilter('');
                                    setFromDate('');
                                    setToDate('');
                                }}
                            >
                                Clear Filters
                            </button>
                        </div>
                    )}
                </div>

                <div className="admin-orders-table-wrapper">
                    {loading && orders.length === 0 ? (
                        <div className="admin-orders-empty-state">
                            <div className="loading-spinner" style={{ margin: '0 auto 20px' }}></div>
                            <p>Fetching orders…</p>
                        </div>
                    ) : error ? (
                        <div className="admin-orders-empty-state" style={{ color: '#ef4444' }}>
                            <h3>Something went wrong</h3>
                            <p style={{ marginBottom: '20px' }}>{error}</p>
                            <button className="admin-action-btn" onClick={fetchOrders}>
                                Try Again
                            </button>
                        </div>
                    ) : filteredOrders.length === 0 ? (
                        <div className="admin-orders-empty-state">
                            <div className="admin-empty-icon">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="11" y1="8" x2="11" y2="14"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg>
                            </div>
                            <h3>No orders found</h3>
                            <p>We couldn't find any orders matching your current filters. Try adjusting your search term or clearing the date range.</p>
                        </div>
                    ) : (
                        <table className="admin-orders-table">
                            <thead>
                                <tr>
                                    <th>Order #</th>
                                    <th>Date</th>
                                    <th>Customer</th>
                                    <th>Total</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredOrders.map(order => (
                                    <tr key={order.id}>
                                        <td data-label="Order #" style={{ fontWeight: 800 }}>
                                            #{order.order_number}
                                            {order.original_order_number && (
                                                <div style={{ fontSize: '0.7rem', fontWeight: 'normal', color: 'var(--ao-text-sub)', marginTop: '4px' }}>
                                                    Original: #{order.original_order_number}
                                                </div>
                                            )}
                                        </td>
                                        <td data-label="Date" style={{ color: 'var(--ao-text-sub)' }}>{formatDate(order.created_at)}</td>
                                        <td data-label="Customer" style={{ fontWeight: 600 }}>{order.customer_name || `ID: ${order.customer_id}`}</td>
                                        <td data-label="Total" style={{ fontWeight: 800 }}>${parseFloat(order.final_amount).toFixed(2)}</td>
                                        <td data-label="Status">
                                            <span className={`admin-status-badge ${getStatusClass(order.status)}`}>
                                                {order.order_number?.startsWith('EXC-')
                                                    ? `Exchange (${order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : 'Pending'})`
                                                    : (order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : '')}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <button
                                                className="admin-action-btn view"
                                                onClick={() => setSelectedOrder(order)}
                                            >
                                                View Details
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </main>

            {/* ORDER DETAILS MODAL */}
            {selectedOrder && (
                <div className="admin-order-modal-overlay" onClick={() => setSelectedOrder(null)}>
                    <div className="admin-order-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <div className="admin-modal-title-group">
                                <h2>Order #{selectedOrder.order_number}</h2>
                                {selectedOrder.original_order_number && (
                                    <p style={{ color: 'var(--ao-accent)', fontWeight: 700, margin: '4px 0', fontSize: '0.85rem' }}>
                                        Original Order: #{selectedOrder.original_order_number}
                                    </p>
                                )}
                                <p>{formatDate(selectedOrder.created_at)} — ID: {selectedOrder.id}</p>
                            </div>

                            <div className="admin-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <select
                                        className="admin-status-select"
                                        style={{ width: 'auto', minWidth: '140px', padding: '6px 12px', fontSize: '0.85rem' }}
                                        value={selectedOrder.status}
                                        onChange={(e) => handleUpdateStatus(selectedOrder.id, e.target.value)}
                                        disabled={updatingStatus}
                                    >
                                        <option value="pending">Pending</option>
                                        <option value="confirmed">Confirmed</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                    {updatingStatus && <span style={{ fontSize: '0.7rem', color: 'var(--ao-accent)', fontWeight: 700 }}>Saving...</span>}
                                </div>
                                <button
                                    className="admin-action-btn"
                                    style={{ background: '#25D366', color: '#fff', border: 'none' }}
                                    onClick={() => handleSendWhatsapp(selectedOrder)}
                                    title="Send WhatsApp Message"
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.414 0 .004 5.411 0 12.046c0 2.121.54 4.192 1.566 6.04L0 24l6.145-1.613a11.793 11.793 0 005.9 1.557h.005c6.632 0 12.042-5.411 12.046-12.047a11.82 11.82 0 00-3.483-8.528" /></svg>
                                </button>
                                <button className="admin-action-btn" onClick={() => setSelectedOrder(null)}>
                                    <IconClose />
                                </button>
                            </div>
                        </div>

                        <div className="admin-modal-body">
                            <div className="admin-modal-left-col">
                                <div className="admin-modal-section">
                                    <h3>Order Items</h3>
                                    <div className="admin-items-list">
                                        {selectedOrder.items?.map((item, idx) => (
                                            <div key={idx} className="admin-item-row">
                                                <img
                                                    src={`${API_BASE_URL}${item.variation_photo || item.product_photo}`}
                                                    alt=""
                                                    className="admin-item-img"
                                                    onError={(e) => { e.target.src = 'https://via.placeholder.com/100'; }}
                                                />
                                                <div className="admin-item-info">
                                                    <span className="admin-item-name">{item.product_name}</span>
                                                    <span className="admin-item-variant">
                                                        {item.variation_color} / {item.variation_size}
                                                        {item.product_type && (
                                                            <>
                                                                <span style={{ margin: '0 6px', color: '#ccc' }}>•</span>
                                                                Fabric: <strong style={{ color: 'var(--ao-accent)', fontWeight: '600' }}>{item.product_type}</strong>
                                                            </>
                                                        )}
                                                    </span>
                                                    <div className="admin-item-qty-price">
                                                        {item.quantity} × ${parseFloat(item.price).toFixed(2)}
                                                        {parseFloat(item.discount || 0) > 0 && !item.offer_qty && (
                                                            <div style={{ color: '#ef4444', fontSize: '0.7rem', marginTop: '2px', fontWeight: 600 }}>
                                                                Discount: -${parseFloat(item.discount).toFixed(2)}
                                                                {item.discount_type && (
                                                                    <span style={{ color: 'var(--ao-text-sub)', fontWeight: 400, marginLeft: '4px' }}>
                                                                        ({item.discount_value}{item.discount_type === 'percentage' ? '%' : '$'} off)
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                        {item.offer_qty && (
                                                            <div style={{ color: '#10b981', fontSize: '0.7rem', marginTop: '2px', fontWeight: 600 }}>
                                                                Offer Applied: {item.offer_qty} for ${parseFloat(item.offer_price).toFixed(2)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="admin-item-total">
                                                    ${parseFloat(item.total).toFixed(2)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                            </div>

                            <div className="admin-modal-right-col">

                                <div className="admin-modal-section">
                                    <h3>Customer</h3>
                                    <div className="admin-order-details-grid">
                                        <div className="admin-detail-item">
                                            <span className="admin-detail-label">Customer ID</span>
                                            <span className="admin-detail-value" style={{ fontSize: '0.85rem', color: 'var(--ao-text-sub)' }}>{selectedOrder.customer_id || 'N/A'}</span>
                                        </div>
                                        <div className="admin-detail-item">
                                            <span className="admin-detail-label">Name</span>
                                            <span className="admin-detail-value">{selectedOrder.customer_name || 'N/A'}</span>
                                        </div>
                                        <div className="admin-detail-item">
                                            <span className="admin-detail-label">Phone</span>
                                            <span className="admin-detail-value">{selectedOrder.customer_phone || 'N/A'}</span>
                                        </div>
                                        <div className="admin-detail-item" style={{ gridColumn: 'span 2' }}>
                                            <span className="admin-detail-label">Email</span>
                                            <span className="admin-detail-value" style={{ fontSize: '0.85rem' }}>{selectedOrder.customer_email || 'N/A'}</span>
                                        </div>
                                        <div className="admin-detail-item" style={{ gridColumn: 'span 2' }}>
                                            <span className="admin-detail-label">Shipping Address</span>
                                            <span className="admin-detail-value" style={{ fontSize: '0.85rem' }}>
                                                {selectedOrder.customer_address1 || ''}
                                                {selectedOrder.customer_address2 ? `, ${selectedOrder.customer_address2}` : ''}
                                                <br />
                                                {selectedOrder.customer_street ? `${selectedOrder.customer_street}, ` : ''}
                                                {selectedOrder.customer_building ? `Bldg: ${selectedOrder.customer_building}, ` : ''}
                                                {selectedOrder.customer_floor ? `Floor: ${selectedOrder.customer_floor}` : ''}
                                                <br />
                                                {selectedOrder.customer_city || ''}
                                                {selectedOrder.customer_country ? `, ${selectedOrder.customer_country}` : ''}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="admin-modal-section">
                                    <h3>Summary</h3>
                                    <div className="admin-order-total-box">
                                        <div className="admin-total-row">
                                            <span>Subtotal</span>
                                            <span>${parseFloat(selectedOrder.total_amount).toFixed(2)}</span>
                                        </div>
                                        <div className="admin-total-row">
                                            <span>
                                                Discount
                                                {selectedOrder.discount_type && (
                                                    <span style={{ fontSize: '0.7rem', color: 'var(--ao-text-sub)', display: 'block' }}>
                                                        ({selectedOrder.discount_type}: {selectedOrder.discount_value}{selectedOrder.discount_type === 'percentage' ? '%' : '$'})
                                                    </span>
                                                )}
                                            </span>
                                            <span style={{ color: '#ef4444' }}>-${parseFloat(selectedOrder.discount_amount || 0).toFixed(2)}</span>
                                        </div>

                                        {parseFloat(selectedOrder.coupon_discount_value || 0) > 0 && (
                                            <div className="admin-total-row">
                                                <span>
                                                    Coupon
                                                    <span style={{ fontSize: '0.7rem', color: 'var(--ao-text-sub)', display: 'block' }}>
                                                        ({selectedOrder.coupon_code}: {selectedOrder.coupon_discount_value}{selectedOrder.coupon_discount_type === 'percentage' ? '%' : '$'})
                                                    </span>
                                                </span>
                                                <span style={{ color: '#ef4444' }}>
                                                    -${(selectedOrder.coupon_discount_type === 'percentage'
                                                        ? (parseFloat(selectedOrder.total_amount) * parseFloat(selectedOrder.coupon_discount_value) / 100)
                                                        : parseFloat(selectedOrder.coupon_discount_value)).toFixed(2)}
                                                </span>
                                            </div>
                                        )}

                                        <div className="admin-total-row">
                                            <span>Delivery</span>
                                            {editingDeliveryFee ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <input
                                                        type="number"
                                                        value={newDeliveryFee}
                                                        onChange={(e) => setNewDeliveryFee(e.target.value)}
                                                        style={{ width: '70px', padding: '4px 6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem' }}
                                                    />
                                                    <button
                                                        onClick={handleSaveDeliveryFee}
                                                        disabled={savingDeliveryFee}
                                                        style={{ padding: '4px 8px', fontSize: '0.75rem', cursor: 'pointer', background: 'var(--ao-accent)', color: '#fff', border: 'none', borderRadius: '4px' }}
                                                    >
                                                        {savingDeliveryFee ? '...' : 'Save'}
                                                    </button>
                                                    <button
                                                        onClick={() => setEditingDeliveryFee(false)}
                                                        disabled={savingDeliveryFee}
                                                        style={{ padding: '4px 8px', fontSize: '0.75rem', cursor: 'pointer', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '4px' }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    {parseFloat(selectedOrder.delivery_fee || 0) === 0 ? (
                                                        <span style={{ color: '#10b981', fontWeight: '700' }}>Free Delivery Earned</span>
                                                    ) : (
                                                        <span>${parseFloat(selectedOrder.delivery_fee || 0).toFixed(2)}</span>
                                                    )}
                                                    <button
                                                        onClick={handleEditDeliveryFee}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ao-accent)', fontSize: '0.8rem', padding: '0', textDecoration: 'underline' }}
                                                    >
                                                        Edit
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <div className="admin-total-row grand">
                                            <span>Total</span>
                                            <span>${parseFloat(selectedOrder.final_amount).toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* CONFIRMATION DIALOG FOR STATUS UPDATE */}
            {pendingStatusUpdate && (
                <div className="admin-order-modal-overlay" style={{ zIndex: 6000 }}>
                    <div className="admin-modal-section" style={{ maxWidth: '400px', width: '90%', textAlign: 'center', padding: '30px' }}>
                        <div style={{ background: 'var(--ao-accent-glow)', color: 'var(--ao-accent)', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                        </div>
                        <h3 style={{ margin: '0 0 10px', fontSize: '1.2rem', color: 'var(--ao-text-main)', textTransform: 'none', letterSpacing: 'normal' }}>
                            Confirm Status Update
                        </h3>
                        <p style={{ margin: '0 0 25px', color: 'var(--ao-text-sub)', fontSize: '0.95rem', lineHeight: '1.5' }}>
                            Are you sure you want to change this order status to
                            <strong style={{ color: 'var(--ao-text-main)', display: 'block', marginTop: '5px' }}>
                                "{pendingStatusUpdate.newStatus.toUpperCase()}"?
                            </strong>
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                className="admin-action-btn clear"
                                style={{ flex: 1, justifyContent: 'center' }}
                                onClick={() => setPendingStatusUpdate(null)}
                            >
                                Cancel
                            </button>
                            <button
                                className="admin-action-btn view"
                                style={{ flex: 1, justifyContent: 'center' }}
                                onClick={executeStatusUpdate}
                                disabled={updatingStatus}
                            >
                                {updatingStatus ? 'Updating...' : 'Confirm Update'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ERROR DIALOG FOR INVALID STATUS TRANSITION */}
            {statusError && (
                <div className="admin-order-modal-overlay" style={{ zIndex: 7000 }}>
                    <div className="admin-modal-section" style={{ maxWidth: '400px', width: '90%', textAlign: 'center', padding: '30px' }}>
                        <div style={{ background: '#fef2f2', color: '#ef4444', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                        </div>
                        <h3 style={{ margin: '0 0 10px', fontSize: '1.2rem', color: 'var(--ao-text-main)', textTransform: 'none', letterSpacing: 'normal' }}>
                            Action Restricted
                        </h3>
                        <p style={{ margin: '0 0 25px', color: 'var(--ao-text-sub)', fontSize: '0.95rem', lineHeight: '1.5' }}>
                            {statusError}
                        </p>
                        <button
                            className="admin-action-btn view"
                            style={{ width: '100%', justifyContent: 'center' }}
                            onClick={() => setStatusError(null)}
                        >
                            Understood
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminOrders;
